/**
 * @file：剧场，直播，音乐，精选视频相关 request
 */
import {
  ISelectionVideos,
  ISubjects,
  ISymptomVideo,
  IPaginationRes,
  ILives,
  IMusicColumn,
  IMusicColumnList,
  ISubject,
  IPaginationSubject,
  ICalendar,
  ICalendars,
  INoticeList,
  ILivesLiveList,
  ILivesDepartment,
  ILivesDepartmentTransform
} from '@schemas/video';
import { ApiConfigs, Response } from '@schemas/service';
import { request } from './request';

const api: ApiConfigs = {
  queryLiveColumn: {
    // url: '/health/api/v1/liveColumn/info',
    // url:
    //   'https://kapi.corp.kuaishou.com/mock/5058/health/api/v1/liveColumn/info',
    // url:
    //  'https://kapi.corp.kuaishou.com/mock/5058/health/api/v1/liveColumn/doctorFaceToFace',
    url: '/health/api/v1/liveColumn/doctorFaceToFace',
    method: 'GET'
  },
  querySeriesColumn: {
    url: '/health/api/v1/album/queryAllAlbums',
    method: 'GET'
  },
  querySeriesDetail: {
    url: '/health/api/v1/album/queryVideosByAlbumId',
    method: 'GET'
  },
  querySelectionVideos: {
    url: '/health/api/v1/contentRecommend/homeFeaturedVideos',
    method: 'GET'
  },
  querySeriesList: {
    url: '/health/api/v1/album/queryAlbumVideos',
    method: 'GET'
  },
  querySymptomVideos: {
    url: '/health/api/v1/medicalCode/symptomsPhotos',
    // url:
    //   'https://kapi.corp.kuaishou.com/mock/5058/health/api/v1/medicalCode/symptomsPhotos',
    method: 'GET'
  },
  querySubjectList: {
    url: '/health/api/v1/home/queryHealthSubject',
    method: 'GET'
  },
  queryTopicListByTag: {
    url: '/health/api/v1/home/queryHealthSubjectByTag',
    method: 'GET'
  },
  querySubjectListByChannel: {
    url: '/health/api/v1/home/queryHealthSubjectByChannel',
    method: 'GET'
  },
  queryLiveDepartments: {
    url: '/health/api/v1/liveColumn/liveDepartments',
    method: 'GET'
  },
  queryLiveList: {
    url: '/health/api/v1/liveColumn/liveInfoByDepartment',
    method: 'GET'
  },
  queryHotHomeVideo: {
    url: '/health/api/v1/home/queryHotHealthVideo',
    method: 'GET'
  },
  queryLiveCalendarList: {
    url: '/health/api/v1/home/queryLiveCalendarList',
    method: 'GET'
  },
  // 直播二级页面”直播中“的视频
  queryLivesLivingVideos: {
    url: '/health/api/v1/liveColumn/liveSecondLevelPage',
    method: 'GET'
  },
  // 直播二级页面”直播回放“的视频
  queryLivesReplayVideos: {
    url: '/health/api/v1/liveColumn/replaySecondLevelPage',
    // url:
    // 'https://kapi.corp.kuaishou.com/mock/5058/health/api/v1/liveColumn/replaySecondLevelPage',
    method: 'GET'
  },
  // 直播二级页面”直播回放“下的科室
  queryLivesReplayDepartment: {
    url: '/health/api/v1/liveColumn/replaySecondLevelPageDepartment',
    // url:
    //  'https://kapi.corp.kuaishou.com/mock/5058/health/api/v1/liveColumn/replaySecondLevelPageDepartment',
    method: 'GET'
  },
  // 直播二级页面”直播预告“列表
  queryLivesNoticeList: {
    url: '/health/api/v1/liveColumn/liveCalendarSecondLevelPage',
    // url: 'https://kapi.corp.kuaishou.com/mock/5058/health/api/v1/liveColumn/liveCalendarSecondLevelPage',
    method: 'GET'
  }
};

export const cacheHandler = {
  getLiveColumn: {
    get: () => {
      const data = window.localStorage.getItem('cacheMap.queryLiveColumn');
      try {
        return data ? JSON.parse(data) : null;
      } catch (error) {
        return null;
      }
    },
    set: (res) => {
      window.localStorage.setItem(
        'cacheMap.queryLiveColumn',
        JSON.stringify(res)
      );
    }
  }
};

const videos = {
  async getLiveColumn() {
    // 添加缓存
    const response: Response<ILives> = await request(
      api.queryLiveColumn,
      undefined,
      undefined,
      { hideGlobalLoading: false }
    );
    const { result = [] } = response;
    if (result) {
      cacheHandler.getLiveColumn.set(result);
      return result;
    }
    return [];
  },

  async getLiveCalendar() {
    const response: Response<ICalendars> = await request(
      api.queryLiveCalendarList
    );
    const { result = [] } = response;
    if (result) {
      return result;
    }
    return [];
  },

  async getSeriesColumn() {
    const response: Response = await request(api.querySeriesColumn);
    const { result = [] } = response;
    return result;
  },

  async getSeriesDetail(pId) {
    const response: Response = await request(api.querySeriesDetail, {
      albumId: pId
    });
    const { result = [] } = response;
    return result;
  },

  async getSeriesList({ pageNum = 1, pageSize = 3 }) {
    const response: Response<IPaginationRes> = await request(
      api.querySeriesList,
      {
        pageNum,
        pageSize
      }
    );
    const { result } = response;
    return result;
  },

  async getSelectionVideos({
    departmentId,
    pageNum = 1,
    pageSize = 10
  }: ISelectionVideos) {
    const response: Response = await request(api.querySelectionVideos, {
      departmentId,
      pageNum,
      pageSize
    });
    const { result = [] } = response;
    return result;
  },

  async getSubjectList(categoryId) {
    try {
      const response: Response<ISubjects> = await request(
        api.querySubjectList,
        {
          categoryId
        }
      );
      const { result = [] } = response;

      return result;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  async getSubjectListByChannel({ channelId, pageNum = 1 }) {
    try {
      const response: Response<ISubject> = await request(
        api.querySubjectListByChannel,
        {
          channelId,
          pageNum
        }
      );
      const { result } = response;
      return result;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  async getSymptomVideos(symptomsId) {
    try {
      const response: Response<ISymptomVideo> = await request(
        api.querySymptomVideos,
        {
          symptomsId
        }
      );
      const { result } = response;
      return result;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  async getTopicListByTag({ tag, pageNum = 1, pageSize = 3 }) {
    try {
      const response: Response<IPaginationSubject> = await request(
        api.queryTopicListByTag,
        {
          tag,
          pageNum,
          pageSize
        }
      );
      const { result } = response;
      return result;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  async getLiveDepartment() {
    try {
      const response: Response = await request(api.queryLiveDepartments);
      const { result } = response;
      if (result) {
        return result;
      }
      return [];
    } catch (error) {
      return Promise.reject(error);
    }
  },

  async getLiveByDepartmentId(departmentId) {
    try {
      const response: Response<ILives> = await request(api.queryLiveList, {
        departmentId
      });
      const { result } = response;
      if (result) {
        return result;
      }
      return [];
    } catch (error) {
      return Promise.reject(error);
    }
  },

  async getHomeHotVideos({ pageNum = 1, pageSize = 10 }) {
    try {
      const response: Response = await request(api.queryHotHomeVideo, {
        pageNum,
        pageSize
      });
      const { result } = response;
      if (result) {
        return result;
      }
      return Promise.reject();
    } catch (error) {
      return Promise.reject(error);
    }
  },

  async getLivesLivingVideos({ pageNum = 1, pageSize = 6 }) {
    try {
      const response: Response<ILivesLiveList | null> = await request(
        api.queryLivesLivingVideos,
        {
          pageNum,
          pageSize
        }
      );
      const { result } = response;
      if (result) {
        return result;
      }
      return {} as ILivesLiveList;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  async getLivesReplayVideos({ pageNum = 1, pageSize = 6, departmentId = 0 }) {
    try {
      const response: Response<ILivesLiveList | null> = await request(
        api.queryLivesReplayVideos,
        {
          pageNum,
          pageSize,
          departmentId
        }
      );
      const { result } = response;
      if (result) {
        return result;
      }
      return {} as ILivesLiveList;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  async getLivesReplayDepartment() {
    try {
      const response: Response<ILivesDepartment[] | null> = await request(
        api.queryLivesReplayDepartment
      );
      const { result } = response;
      if (result) {
        const departmentTabs: ILivesDepartmentTransform[] = result.map(
          (item) => ({
            key: item.departmentId,
            value: item.departmentName
          })
        );
        return departmentTabs;
      }
      return [];
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async getLivesNoticeList({ pageNum = 1, pageSize = 15 }) {
    try {
      const response: Response<INoticeList | null> = await request(
        api.queryLivesNoticeList,
        {
          pageNum,
          pageSize
        }
      );
      const { result } = response;
      if (result) {
        return result;
      }
      return {} as INoticeList;
    } catch (error) {
      return Promise.reject(error);
    }
  }
};

export default videos;
